/*
 * MySiteCare
 *
 * Copyright © 2026 Jeanjen Sisoy Luberio
 * doing business as MySiteCare Website Application Services.
 * All rights reserved.
 *
 * DTI Business Name No. 7934250
 * https://mysitecare.eu
 *
 * This file contains original proprietary MySiteCare source code,
 * except for third-party components identified under their
 * respective licenses.
 */
import { useCallback, useEffect, useState } from "react";
import { AlertTriangle, CheckCircle2, LifeBuoy, RefreshCw, Send, Sparkles } from "lucide-react";
import { AI_SETTINGS_CHANGED } from "@/components/AiGuardianPanel";
import {
  aiErrorMessage, editSupportDraft, escalateSupportDraft, fetchAiStatus, fetchSupportDrafts, fetchSupportTickets, generateSupportDraft, sendSupportDraft,
} from "@/lib/aiApi";

const OPEN_STATES = ["open", "reopened", "in_progress", "waiting_customer"];

function formatWhen(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString();
}

function Notice({ tone = "amber", children, ...rest }) {
  const tones = {
    amber: "border-amber-200 bg-amber-50 text-amber-900",
    rose: "border-rose-200 bg-rose-50 text-rose-800",
    emerald: "border-emerald-200 bg-emerald-50 text-emerald-800",
    slate: "border-slate-200 bg-slate-50 text-slate-700",
  };
  return <p className={`mt-3 rounded-lg border p-2 text-xs ${tones[tone]}`} {...rest}>{children}</p>;
}

/** The draft for one ticket: facts the reply may rely on, what could not be confirmed, then edit / regenerate / send / escalate. */
export function SupportDraftEditor({ draft: initial, ticket, token, onSent, onChanged }) {
  const [draft, setDraft] = useState(initial);
  const [text, setText] = useState(initial.edited_reply || initial.suggested_reply);
  const [guidance, setGuidance] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [confirmSend, setConfirmSend] = useState(false);
  const [acknowledged, setAcknowledged] = useState(false);

  useEffect(() => {
    setDraft(initial);
    setText(initial.edited_reply || initial.suggested_reply);
    setConfirmSend(false);
    setAcknowledged(false);
  }, [initial]);

  const locked = draft.status !== "draft";
  const warnings = draft.warnings || [];
  const dirty = text.trim() !== (draft.edited_reply || draft.suggested_reply).trim();
  const confidencePct = typeof draft.confidence === "number" ? Math.round(draft.confidence * 100) : null;

  const guard = async (fn, doneMessage) => {
    setBusy(true);
    setError("");
    setInfo("");
    try {
      const result = await fn();
      if (doneMessage) setInfo(doneMessage);
      return result;
    } catch (err) {
      setError(aiErrorMessage(err));
      return null;
    } finally {
      setBusy(false);
    }
  };

  const saveEdit = async () => {
    const saved = await guard(() => editSupportDraft(token, draft.id, text), "Your edit is saved. Nothing has been sent.");
    if (saved) { setDraft(saved); setAcknowledged(false); }
    return saved;
  };

  const regenerate = async () => {
    const fresh = await guard(() => generateSupportDraft(token, ticket.id, { instructions: guidance, regenerate: true }), "Prepared a new draft. Nothing has been sent.");
    if (fresh) onChanged && onChanged(fresh);
  };

  const send = async () => {
    if (dirty) {
      const saved = await saveEdit();
      if (!saved) return;
    }
    const sent = await guard(() => sendSupportDraft(token, draft.id, acknowledged));
    if (sent) {
      setDraft(sent);
      setConfirmSend(false);
      setInfo(`Reply sent to the customer (email status: ${sent.notification_status || "queued"}).`);
      onSent && onSent(sent);
    }
  };

  const escalate = async () => {
    const out = await guard(() => escalateSupportDraft(token, draft.id, "Escalated from the AI Support Copilot."), "Escalated. The ticket is marked In progress for a person to handle.");
    if (out) { setDraft(out); onChanged && onChanged(out); }
  };

  const btn = "inline-flex items-center gap-1.5 rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50";

  return (
    <div className="mt-3 rounded-lg border border-slate-200 bg-white p-4" data-testid="support-draft">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm font-semibold">Suggested reply <span className="font-normal text-slate-400">· draft {draft.version} · {draft.provider} ({draft.model})</span></p>
        <div className="flex flex-wrap items-center gap-2 text-xs">
          {confidencePct !== null && <span className="rounded-full border border-slate-300 bg-slate-50 px-2.5 py-0.5 font-semibold text-slate-700" data-testid="support-confidence">Confidence {confidencePct}%</span>}
          {draft.status !== "draft" && <span className="rounded-full border border-slate-300 bg-slate-100 px-2.5 py-0.5 font-semibold text-slate-700" data-testid="support-draft-status">{draft.status}</span>}
        </div>
      </div>

      {draft.needs_human_review && (
        <Notice tone="amber" data-testid="support-review-banner">
          <AlertTriangle size={12} className="mr-1 inline" /> This question touches money, tax, legal, compliance or security. Read it carefully and edit it before sending — the Copilot cannot decide these.
        </Notice>
      )}

      <label htmlFor={`reply-${draft.id}`} className="sr-only">Reply to the customer</label>
      <textarea
        id={`reply-${draft.id}`} value={text} onChange={(e) => setText(e.target.value)} disabled={locked || busy} rows={9}
        className="mt-3 w-full rounded-lg border border-slate-300 p-3 text-sm leading-relaxed disabled:bg-slate-50" data-testid="support-reply-text"
      />

      {warnings.length > 0 && (
        <div className="mt-2 rounded-lg border border-rose-200 bg-rose-50 p-3" role="alert" data-testid="support-warnings">
          <p className="text-xs font-semibold text-rose-800">Check these before sending — the account records do not back them up:</p>
          <ul className="mt-1 list-disc space-y-0.5 pl-5 text-xs text-rose-800">
            {warnings.map((w, i) => <li key={`${w.code}-${i}`}>{w.message}</li>)}
          </ul>
        </div>
      )}

      <div className="mt-3 grid gap-3 sm:grid-cols-2">
        <div data-testid="support-context">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Relevant account context</p>
          <ul className="mt-1 space-y-1 text-xs text-slate-700">
            {(draft.facts || []).filter((f) => f.verified).map((f) => (
              <li key={f.key}><CheckCircle2 size={11} className="mr-1 inline text-emerald-600" /><span className="font-semibold">{f.label}:</span> {f.value}</li>
            ))}
          </ul>
        </div>
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Recommended action</p>
          <p className="mt-1 text-xs text-slate-700" data-testid="support-recommended-action">{draft.recommended_action}</p>
          {(draft.unverifiable_topics || []).length > 0 && (
            <div className="mt-2" data-testid="support-cannot-confirm">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Cannot confirm from our records</p>
              <ul className="mt-1 list-disc space-y-0.5 pl-5 text-xs text-slate-700">{draft.unverifiable_topics.map((t) => <li key={t.topic}>{t.topic}</li>)}</ul>
            </div>
          )}
        </div>
      </div>

      {!locked && (
        <div className="mt-3">
          <label htmlFor={`guidance-${draft.id}`} className="text-xs font-semibold text-slate-500">Guidance for a new draft (optional)</label>
          <input
            id={`guidance-${draft.id}`} value={guidance} onChange={(e) => setGuidance(e.target.value)} placeholder="e.g. Keep it shorter and friendlier"
            className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-1.5 text-sm" maxLength={500} data-testid="support-guidance"
          />
        </div>
      )}

      {error && <Notice tone="rose" role="alert" data-testid="support-error">{error}</Notice>}
      {info && <Notice tone="emerald" role="status" data-testid="support-info">{info}</Notice>}

      {!locked && (
        <div className="mt-3 flex flex-wrap items-center gap-2">
          {!confirmSend ? (
            <button className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-emerald-700 disabled:opacity-50" disabled={busy || !text.trim()} onClick={() => setConfirmSend(true)} data-testid="support-send">
              <Send size={13} /> Send
            </button>
          ) : (
            <div className="flex flex-wrap items-center gap-2" data-testid="support-send-confirm-row">
              <span className="text-xs text-slate-600">Email this reply to the customer now?</span>
              {warnings.length > 0 && (
                <label className="flex items-center gap-1.5 text-xs text-rose-800">
                  <input type="checkbox" checked={acknowledged} onChange={(e) => setAcknowledged(e.target.checked)} data-testid="support-acknowledge" />
                  I checked these claims against the account records
                </label>
              )}
              <button className="rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-emerald-700 disabled:opacity-50" disabled={busy || (warnings.length > 0 && !acknowledged)} onClick={send} data-testid="support-send-confirm">
                {busy ? "Sending…" : "Yes, send"}
              </button>
              <button className={btn} disabled={busy} onClick={() => setConfirmSend(false)}>Cancel</button>
            </div>
          )}
          <button className={btn} disabled={busy || !dirty} onClick={saveEdit} data-testid="support-edit">Save edit</button>
          <button className={btn} disabled={busy} onClick={regenerate} data-testid="support-regenerate"><RefreshCw size={13} /> Regenerate</button>
          <button className={btn} disabled={busy} onClick={escalate} data-testid="support-escalate">Escalate</button>
        </div>
      )}
      {locked && draft.status === "sent" && <Notice tone="slate">Sent by {draft.sent_by} on {formatWhen(draft.sent_at)}.</Notice>}
      {locked && draft.status === "escalated" && <Notice tone="slate">Escalated by {draft.escalation?.by} on {formatWhen(draft.escalation?.at)}.</Notice>}
    </div>
  );
}

// ---------------------------------------------------------------------------------------------------------------
export default function AiSupportCopilotPanel({ token }) {
  const [status, setStatus] = useState(null);
  const [tickets, setTickets] = useState(null);
  const [selected, setSelected] = useState(null);
  const [draft, setDraft] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [listError, setListError] = useState("");

  const load = useCallback(() => {
    if (!token) return;
    fetchAiStatus(token).then(setStatus).catch(() => setStatus(null));
    fetchSupportTickets(token)
      .then((rows) => { setTickets(rows.filter((t) => OPEN_STATES.includes(t.status))); setListError(""); })
      .catch((err) => { setTickets([]); setListError(aiErrorMessage(err, "The support requests could not be loaded.")); });
  }, [token]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    // refresh when the AI Website Guardian card changes a setting (e.g. turns the Copilot on)
    const refresh = () => { if (token) fetchAiStatus(token).then(setStatus).catch(() => {}); };
    window.addEventListener(AI_SETTINGS_CHANGED, refresh);
    return () => window.removeEventListener(AI_SETTINGS_CHANGED, refresh);
  }, [token]);

  const choose = async (ticket) => {
    setSelected(ticket);
    setDraft(null);
    setError("");
    try {
      const existing = await fetchSupportDrafts(token, ticket.id);
      const current = existing.find((d) => d.status === "draft");
      if (current) setDraft(current);
    } catch (err) {
      setError(aiErrorMessage(err));
    }
  };

  const prepare = async () => {
    setBusy(true);
    setError("");
    try {
      setDraft(await generateSupportDraft(token, selected.id));
    } catch (err) {
      setError(aiErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const enabled = !!status?.support_enabled;

  return (
    <section className="mt-4 rounded-xl border border-violet-200 bg-white p-5" data-testid="ai-support-panel" aria-labelledby="ai-support-heading">
      <h3 id="ai-support-heading" className="flex items-center gap-2 font-semibold"><LifeBuoy size={18} className="text-violet-600" /> AI Support Copilot</h3>
      <p className="mt-1 max-w-prose text-xs text-slate-500">
        Prepares a draft reply from the customer’s real account records. It never sends anything by itself — you read it, edit it and press Send.
      </p>

      {status && !enabled && (
        <Notice tone="slate" data-testid="support-disabled">The Support Copilot is off. Turn it on in the AI Website Guardian card above to start preparing drafts.</Notice>
      )}
      {listError && <Notice tone="rose" role="alert">{listError}</Notice>}

      <div className="mt-3 grid gap-4 lg:grid-cols-[minmax(0,2fr)_minmax(0,3fr)]">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Open support requests</p>
          <ul className="mt-1 max-h-72 space-y-1 overflow-y-auto" data-testid="support-ticket-list">
            {!tickets ? <li className="text-sm text-slate-500">Loading…</li> : tickets.length === 0 ? <li className="text-sm text-slate-500">No open support requests.</li> : tickets.map((t) => (
              <li key={t.id}>
                <button
                  onClick={() => choose(t)} aria-pressed={selected?.id === t.id}
                  className={`w-full rounded-lg border px-3 py-2 text-left text-sm ${selected?.id === t.id ? "border-violet-400 bg-violet-50" : "border-slate-200 hover:bg-slate-50"}`}
                  data-testid="support-ticket-item"
                >
                  <span className="block font-semibold text-slate-800">{t.subject}</span>
                  <span className="block text-xs text-slate-500">{t.website_slug} · {t.status.replace(/_/g, " ")} · {formatWhen(t.updated_at)}</span>
                </button>
              </li>
            ))}
          </ul>
        </div>

        <div data-testid="support-workspace">
          {!selected ? <p className="text-sm text-slate-500">Pick a request to prepare a reply.</p> : (
            <>
              <p className="text-sm font-semibold">{selected.subject}</p>
              <ul className="mt-1 max-h-40 space-y-1 overflow-y-auto rounded-lg bg-slate-50 p-2 text-xs text-slate-700" data-testid="support-thread">
                {(selected.messages || []).slice(-4).map((m) => (
                  <li key={m.id}><span className="font-semibold">{m.author_type === "staff" ? "MySiteCare" : "Customer"}:</span> {m.message}</li>
                ))}
              </ul>
              {!draft && (
                <button
                  onClick={prepare} disabled={busy || !enabled}
                  className="mt-3 inline-flex items-center gap-1.5 rounded-lg border border-violet-300 bg-white px-3 py-1.5 text-xs font-semibold text-violet-800 hover:bg-violet-50 disabled:opacity-50"
                  data-testid="support-prepare"
                >
                  <Sparkles size={13} /> {busy ? "Preparing…" : "Prepare a reply"}
                </button>
              )}
              {error && <Notice tone="amber" role="status" data-testid="support-panel-error">{error}</Notice>}
              {draft && (
                <SupportDraftEditor
                  draft={draft} ticket={selected} token={token}
                  onChanged={(next) => setDraft(next)}
                  onSent={() => { load(); }}
                />
              )}
            </>
          )}
        </div>
      </div>
    </section>
  );
}
